import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHero } from "../components/subcomponents/PageHero";
import bigCover from "../assets/images/big-cover.jpg";
import { apiGet } from "../utils/api";
import { Colors } from "../utils/utils.colors";
import { routes } from "../utils/utils.routes";
import { PageSkeleton } from "../components/PageSkeleton";
import { FaGraduationCap, FaCertificate, FaCalendarAlt, FaBookOpen } from "react-icons/fa";
import { useLanguage } from "../contexts/LanguageContext";
import { pickLocalized } from "../utils/locale";
import { PageSectionHeader } from "../components/subcomponents/PageSectionHeader";

const GradI = FaGraduationCap as any;
const CertI = FaCertificate as any;
const CalI = FaCalendarAlt as any;
const BookI = FaBookOpen as any;

export const SpecialProgramDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t } = useLanguage();
  const [program, setProgram] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    apiGet(`/special-programs/${slug}`)
      .then((d) => setProgram(d.item || d))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PageSkeleton />;
  if (!program) {
    return (
      <div style={{ padding: 80, textAlign: "center" }}>
        <h2>{t.specialProgram?.notFound || "Programme introuvable"}</h2>
        <Link to={routes.FACULTIES} style={{ color: Colors.primaryColor, fontWeight: 600 }}>
          ← {t.faculty.allFaculties}
        </Link>
      </div>
    );
  }

  const name = pickLocalized(program, "name", lang);
  const description = pickLocalized(program, "description", lang);
  const cover = program.coverImage || bigCover;

  const allContents: any[] = (program.contents || []).filter((c: any) => c.status === "published");
  const events = allContents.filter((c: any) => c.type === "event" || c.type === "activity");
  const articles = allContents.filter((c: any) => c.type === "article");
  const isOnlineCourse = (c: any) => {
    const cat = (c.category || "").toLowerCase();
    return cat.includes("cours") || cat.includes("course") || cat.includes("mooc") || cat.includes("e-learning");
  };
  const courses = allContents.filter(isOnlineCourse);
  const publications = articles.filter((c: any) => !isOnlineCourse(c));

  const facultyName = program.faculty ? pickLocalized(program.faculty, "name", lang) : "";

  return (
    <>
      <PageHero
        imageCover={cover}
        eyebrow={t.faculty.specialPrograms}
        title={name}
        tall
        breadcrumbs={[
          { label: t.faculty.allFaculties, href: routes.FACULTIES },
          ...(program.faculty
            ? [{ label: facultyName, href: `${routes.FACULTIES}/${program.faculty.slug}` }]
            : []),
          { label: name },
        ]}
      />

      <div className="wp-block-group padding-x is-layout-constrained faculty-page">
        <div className="faculty-detail-layout">
          <div>
            {/* --- Presentation --- */}
            <section style={{ marginBottom: 40 }}>
              <PageSectionHeader
                id="special-program-presentation"
                eyebrow={t.specialProgram?.eyebrowPresentation || "Présentation"}
                title={name}
                lead={
                  description ? (
                    <div className="rich-content" dangerouslySetInnerHTML={{ __html: description }} />
                  ) : (
                    <p style={{ color: "#888" }}>—</p>
                  )
                }
                align="start"
              />
            </section>

            {/* --- Publications --- */}
            {publications.length > 0 && (
              <section style={{ marginBottom: 40 }}>
                <PageSectionHeader
                  id="special-program-publications"
                  eyebrow={t.specialProgram?.eyebrowPublications || "Publications"}
                  title={t.faculty.publications}
                  align="start"
                />
                <div style={{ display: "grid", gap: 12 }}>
                  {publications.map((c: any) => (
                    <Link
                      key={c.id}
                      to={`${routes.ARTICLESDETAILS}/${c.slug}`}
                      style={{
                        display: "flex", gap: 16, background: "#fff", padding: 16, borderRadius: 12,
                        textDecoration: "none", color: "inherit", border: "1px solid #e8ecf1",
                        transition: "box-shadow .2s",
                      }}
                      className="sp-content-card"
                    >
                      {c.coverImage && (
                        <img src={c.coverImage} alt="" style={{ width: 120, height: 88, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontSize: 11, color: Colors.primaryColor, textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                          <CertI size={12} /> {c.category || t.faculty.publications}
                        </div>
                        <h5 style={{ margin: "6px 0 4px", color: Colors.darkColor, fontSize: 16 }}>{c.title}</h5>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{(c.excerpt || "").slice(0, 140)}</p>
                        <span style={{ fontSize: 12, color: "#999", marginTop: 6, display: "inline-block" }}>
                          {new Date(c.createdAt).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR")}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* --- Courses --- */}
            {courses.length > 0 && (
              <section style={{ marginBottom: 40 }}>
                <PageSectionHeader
                  id="special-program-courses"
                  eyebrow={t.specialProgram?.eyebrowCourses || "Formation"}
                  title={t.faculty.onlineCourses}
                  align="start"
                />
                <div style={{ display: "grid", gap: 12 }}>
                  {courses.map((c: any) => (
                    <Link
                      key={c.id}
                      to={`${routes.ARTICLESDETAILS}/${c.slug}`}
                      style={{
                        display: "flex", gap: 16, background: "#fff", padding: 16, borderRadius: 12,
                        textDecoration: "none", color: "inherit", border: "1px solid #e8ecf1",
                      }}
                      className="sp-content-card"
                    >
                      {c.coverImage && (
                        <img src={c.coverImage} alt="" style={{ width: 120, height: 88, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontSize: 11, color: "#0891b2", textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                          <BookI size={12} /> {t.faculty.onlineCourses}
                        </div>
                        <h5 style={{ margin: "6px 0 4px", color: Colors.darkColor, fontSize: 16 }}>{c.title}</h5>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{(c.excerpt || "").slice(0, 140)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* --- Events --- */}
            {events.length > 0 && (
              <section style={{ marginBottom: 40 }}>
                <PageSectionHeader
                  id="special-program-events"
                  eyebrow={t.specialProgram?.eyebrowEvents || "Événements"}
                  title={t.faculty.events}
                  align="start"
                />
                <div style={{ display: "grid", gap: 12 }}>
                  {events.map((c: any) => (
                    <Link
                      key={c.id}
                      to={`${routes.ARTICLESDETAILS}/${c.slug}`}
                      style={{
                        display: "flex", gap: 16, background: "#fff", padding: 16, borderRadius: 12,
                        textDecoration: "none", color: "inherit", border: "1px solid #e8ecf1",
                      }}
                      className="sp-content-card"
                    >
                      {c.coverImage && (
                        <img src={c.coverImage} alt="" style={{ width: 120, height: 88, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontSize: 11, color: "#f59e0b", textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                          <CalI size={12} /> {c.type === "event" ? t.faculty.events : c.category}
                        </div>
                        <h5 style={{ margin: "6px 0 4px", color: Colors.darkColor, fontSize: 16 }}>{c.title}</h5>
                        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{(c.excerpt || "").slice(0, 140)}</p>
                        {c.eventDate && (
                          <span style={{ fontSize: 12, color: "#999", marginTop: 6, display: "inline-block" }}>
                            {new Date(c.eventDate).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR")}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* --- Empty state --- */}
            {allContents.length === 0 && (
              <section style={{ marginBottom: 40 }}>
                <div style={{ textAlign: "center", padding: "40px 20px", background: "#f8fafc", borderRadius: 12, border: "1px dashed #e2e8f0" }}>
                  <GradI size={40} color={Colors.secondaryColor} />
                  <p style={{ color: "#94a3b8", marginTop: 16, fontSize: 15 }}>
                    {t.specialProgram?.noContent || "Aucune publication pour ce programme pour le moment."}
                  </p>
                </div>
              </section>
            )}
          </div>

          {/* --- Sidebar --- */}
          <aside>
            <div className="faculty-sidebar-card faculty-sidebar-card--primary">
              <h4 style={{ color: "#fff", marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
                {t.specialProgram?.info || "Informations"}
              </h4>
              {program.faculty && (
                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <GradI size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{t.admin.faculty}</div>
                    <Link
                      to={`${routes.FACULTIES}/${program.faculty.slug}`}
                      style={{ fontWeight: 600, fontSize: 15, color: "#fff", textDecoration: "underline" }}
                    >
                      {facultyName}
                    </Link>
                  </div>
                </div>
              )}
              {/* <div style={{ display: "flex", gap: 12 }}>
                <CertI size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>{t.specialProgram?.contentCount || "Contenus"}</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{allContents.length}</div>
                </div>
              </div> */}
            </div>
            {program.faculty && (
              <Link
                to={`${routes.FACULTIES}/${program.faculty.slug}`}
                style={{ color: Colors.primaryColor, textDecoration: "none", fontWeight: 600, fontSize: 14 }}
              >
                ← {t.specialProgram?.backToFaculty || "Retour à la faculté"}
              </Link>
            )}
          </aside>
        </div>
      </div>
    </>
  );
};
